'use client';

/**
 * The hand guide — two stylised hands that light up the finger to use.
 *
 * A colour on a keyboard tells a child *where* to press. It does not tell them
 * *what to press it with*, and "use your ring finger" is meaningless to a
 * seven-year-old who has never counted their fingers. So the guide shows a
 * physical hand with one finger raised, in the same colour as the key.
 *
 * Drawn as inline SVG: it scales to any size, themes with CSS variables, needs
 * no image request, and stays crisp when printed onto the parent report.
 */
import React, { memo } from 'react';
import { FINGERS, fingerForChar, needsShift, shiftKeyForChar } from '../lib/keyboard';
import type { FingerId } from '../types';

/** Geometry for one hand, pinky → thumb. Mirrored for the right hand. */
const DIGITS: { id: FingerId; x: number; y: number; w: number; h: number }[] = [
  { id: 'l-pinky', x: 8, y: 44, w: 13, h: 34 },
  { id: 'l-ring', x: 25, y: 28, w: 14, h: 50 },
  { id: 'l-middle', x: 43, y: 21, w: 14, h: 57 },
  { id: 'l-index', x: 61, y: 30, w: 14, h: 48 },
];

const MIRROR: Record<FingerId, FingerId> = {
  'l-pinky': 'r-pinky',
  'l-ring': 'r-ring',
  'l-middle': 'r-middle',
  'l-index': 'r-index',
  'r-pinky': 'l-pinky',
  'r-ring': 'l-ring',
  'r-middle': 'l-middle',
  'r-index': 'l-index',
  thumb: 'thumb',
};

function Hand({
  side,
  active,
  thumbActive,
  pinkyShift,
}: {
  side: 'left' | 'right';
  active: FingerId | null;
  thumbActive: boolean;
  pinkyShift: boolean;
}) {
  return (
    <g transform={side === 'right' ? 'translate(240,0) scale(-1,1)' : undefined}>
      {/* palm */}
      <rect
        x="6"
        y="70"
        width="76"
        height="46"
        rx="16"
        fill="var(--card)"
        stroke="var(--faint)"
        strokeWidth="2.5"
      />

      {DIGITS.map((digit) => {
        const id = side === 'right' ? MIRROR[digit.id] : digit.id;
        const isActive = active === id;
        const isShiftFinger = pinkyShift && id.endsWith('pinky');
        const lit = isActive || isShiftFinger;
        const color = `var(${FINGERS[id].cssVar})`;
        return (
          <g
            key={id}
            style={{ transition: 'transform 180ms cubic-bezier(0.34,1.56,0.64,1)' }}
            transform={lit ? 'translate(0,-7)' : undefined}
          >
            <rect
              x={digit.x}
              y={digit.y}
              width={digit.w}
              height={digit.h}
              rx={digit.w / 2}
              fill={lit ? color : 'var(--card)'}
              stroke={lit ? color : 'var(--faint)'}
              strokeWidth="2.5"
              opacity={1}
            />
            {lit && (
              <circle
                cx={digit.x + digit.w / 2}
                cy={digit.y + 8}
                r="3.2"
                fill="#fff"
                opacity="0.9"
              />
            )}
          </g>
        );
      })}

      {/* thumb */}
      <g
        transform={thumbActive ? 'translate(4,-3)' : undefined}
        style={{ transition: 'transform 180ms cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        <rect
          x="78"
          y="76"
          width="30"
          height="14"
          rx="7"
          transform="rotate(24 78 76)"
          fill={thumbActive ? 'var(--f-thumb)' : 'var(--card)'}
          stroke={thumbActive ? 'var(--f-thumb)' : 'var(--faint)'}
          strokeWidth="2.5"
        />
      </g>
    </g>
  );
}

export interface HandsGuideProps {
  /** The character to coach. */
  char?: string;
  className?: string;
  /** Hide the written instruction and show only the diagram. */
  compact?: boolean;
}

function HandsGuide({ char, className = '', compact = false }: HandsGuideProps) {
  const active = char && char !== ' ' ? fingerForChar(char) : null;
  const thumbActive = char === ' ';
  const shift = char ? shiftKeyForChar(char) : null;

  const activeLeft = active && FINGERS[active].hand === 'left' ? active : null;
  const activeRight = active && FINGERS[active].hand === 'right' ? active : null;

  const label = !char
    ? 'Rest all eight fingers on the home row.'
    : char === ' '
      ? 'Tap the space bar with your thumb.'
      : `${needsShift(char) ? `Hold ${shift === 'ShiftLeft' ? 'left' : 'right'} Shift, then ` : ''}press ${
          char === '\n' ? 'Enter' : char.toUpperCase()
        } with your ${FINGERS[active as FingerId].label}.`;

  return (
    <figure className={className}>
      <svg
        /* 240 wide, not 200: each hand occupies x 6–108 including its rotated
           thumb, so mirroring about 200 made the two thumbs overlap. */
        viewBox="0 0 240 124"
        className="w-full h-auto"
        role="img"
        aria-label={label}
        style={{ maxHeight: 150 }}
      >
        <Hand
          side="left"
          active={activeLeft}
          thumbActive={thumbActive}
          pinkyShift={shift === 'ShiftLeft'}
        />
        <Hand
          side="right"
          active={activeRight}
          thumbActive={thumbActive}
          pinkyShift={shift === 'ShiftRight'}
        />
      </svg>
      {!compact && (
        <figcaption
          className="text-center font-extrabold mt-2 px-3"
          style={{ fontSize: '1.05rem' }}
        >
          {label}
        </figcaption>
      )}
    </figure>
  );
}

export default memo(HandsGuide);
